import { useDashboard } from '@vrplatform/data';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { useAutomationsQuery } from '@vrplatform/data';
import { useAccountingConnectionQuery } from '@vrplatform/data';
import {
  getAutomationInput,
  useAutomationHistoryDrawer,
} from './useAutomationHistoryDrawer';

type AutomationInputType =
  | 'finalytic.reservation'
  | 'finalytic.payment'
  | 'finalytic.listing'
  | 'finalytic.ownerStatement';

export const useAutomationModal = (type: AutomationInputType) => {
  const { data: automations = [] } = useAutomationsQuery();
  const { postAutomations } = useAutomationHistoryDrawer();
  const { accounting, accountingPlatforms } = useAccountingConnectionQuery();

  const [opened, handlers] = useDisclosure(false);

  const [dashboard] = useDashboard();

  return useMemo(() => {
    const filteredAutomations = automations.filter((aut) => {
      const isAutomationOfCorrectType =
        getAutomationInput(aut?.template).schema === type;

      const isPublicAutomation =
        aut?.template.visibility?.toLowerCase() === 'public';

      let isAccounting = true;

      const hasAccountingApp = accountingPlatforms.some((x) =>
        Object.keys(aut.connections).includes(x.appId || '')
      );

      if (hasAccountingApp) {
        isAccounting = [aut.leftConnectionId, aut.rightConnectionId].some(
          (x) => x === accounting?.id
        );
      }

      const isActive = aut.status === 'active';

      return (
        isAutomationOfCorrectType &&
        isPublicAutomation &&
        isAccounting &&
        isActive
      );
    });

    return {
      hasAutomations: dashboard !== 'owner' && filteredAutomations.length > 0,
      automations: filteredAutomations,
      postAutomations: async (params: Parameters<typeof postAutomations>[0]) =>
        postAutomations(params).then(handlers.close),
      opened,
      handlers,
    };
  }, [
    automations,
    dashboard,
    postAutomations,
    handlers,
    opened,
    accounting?.id,
    accountingPlatforms,
    type,
  ]);
};
